import { createContext, useCallback, useContext, useMemo } from "react"
import { ReactComponent as UtilizationIcon } from "../../../../assets/icons/map/filter_utilization.svg"
import { FilterIds, useFilterQueryParams } from "../../hooks/filter_query_params"
import { useMapViewData } from "../../hooks/map_view_data"
import { isEmptyObject } from "@ciq/shared/utils/object"
import eventBus from "@ciq/shared/hooks/eventBus"
import { buildColorRanges } from "../../utils/colors"

const OverlayDataContext = createContext()

export function OverlayDataProvider({ children }) {
  const data = OverlayData()
  return <OverlayDataContext.Provider value={data}>{children}</OverlayDataContext.Provider>
}

export function useOverlayData() {
  const context = useContext(OverlayDataContext)
  if (!context) {
    throw new Error("useOverlayData must be used within an OverlayDataProvider")
  }
  return context
}

const OverlayData = () => {
  const { semesterSummary, hourlySummary } = useMapViewData()
  const { mapDisplayType, onMapDisplayTypeChange } = useFilterQueryParams()

  const { column: semesterColumn, maxValues: semesterMaxValues } = semesterSummary
  const { column: hourColumn, maxValues: hourMaxValues } = hourlySummary

  const filters = useMemo(() => {
    const handleIconClick = key => () => onMapDisplayTypeChange(key)

    const build = (id, label, icon) => ({
      id,
      data: {
        selected: mapDisplayType === id,
        label,
        icon,
        onClick: handleIconClick(id),
      },
    })

    return [
      build(FilterIds.Options.SEMESTER, "Semester", <UtilizationIcon />),
      build(FilterIds.Options.HOURLY, "Hourly", <UtilizationIcon />),
    ]
  }, [mapDisplayType, onMapDisplayTypeChange])

  const hourLegendData = useMemo(() => {
    if (isEmptyObject(hourColumn)) {
      return null
    }

    if (isEmptyObject(hourMaxValues)) {
      return buildLegendData(hourColumn, {})
    }

    return buildLegendData(hourColumn, hourMaxValues)
  }, [hourColumn, hourMaxValues])

  const semesterLegendData = useMemo(() => {
    if (isEmptyObject(semesterColumn)) {
      return null
    }

    if (isEmptyObject(semesterMaxValues)) {
      return buildLegendData(semesterColumn, {})
    }

    return buildLegendData(semesterColumn, semesterMaxValues)
  }, [semesterColumn, semesterMaxValues])

  return {
    setMapPaddingOffset: useCallback(padding => {
      eventBus.emit("map-padding-changed", padding)
    }, []),
    filters,
    legendData: mapDisplayType === FilterIds.Options.SEMESTER ? semesterLegendData : hourLegendData,
  }
}

export const buildLegendData = (column, maxValues) => {
  const maxValue = maxValues[column.name]
  if (maxValue == null) {
    return {
      name: column.label,
      hasData: false,
      colors: [],
    }
  }

  const colorRanges = buildColorRanges(column, maxValue)

  return {
    name: column.label,
    hasData: true,
    colors: colorRanges,
  }
}
